import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStore } from "react-redux";
import { deviceApi } from "../actions/devicesInQuery";
import { realtimeApi } from "../actions/realtimeInQuery";
import type { AppDispatch, RootState } from "../reducers";
import { getActiveOrg } from "../utils/activeOrg";
import { getAccessToken } from "./auth0-instance";
import { createDeviceStateHandler } from "./realtime/deviceState";
import { isDeviceStateEnvelope, RealtimeEnvelope } from "./realtime/envelope";
import { RealtimeSocket } from "./realtime/realtimeSocket";
import { readJwtExpiry } from "./realtime/tokenExpiry";

/**
 * The realtime socket's address, or undefined when this build has none.
 *
 * REACT_APP_REALTIME_ENDPOINT is scheme-less, like REACT_APP_BACKEND_ENDPOINT,
 * and gets its scheme the same way: ws:// in dev, wss:// everywhere else. Left
 * unset, nothing connects and every poller runs as it always has.
 */
export const getRealtimeURL = (): string | undefined => {
  const endpoint = process.env.REACT_APP_REALTIME_ENDPOINT?.trim();
  if (!endpoint) return undefined;
  const protocol = process.env.REACT_APP_ENV !== "dev" ? "wss://" : "ws://";
  return protocol + endpoint;
};

const RealtimeContext = createContext({ connected: false });

/** Whether the realtime socket is open, and so whether live updates arrive. */
export const useRealtimeConnected = () => useContext(RealtimeContext).connected;

/**
 * `ms` while the socket is down, and no polling while it is up.
 *
 * For the pollers the socket replaces. Every (re)connect refetches, so what was
 * missed while down is picked up the moment it comes back; outside a
 * RealtimeProvider, or with no endpoint configured, this is always `ms`.
 */
export const useFallbackPollingInterval = (ms: number) =>
  useRealtimeConnected() ? 0 : ms;

/**
 * One realtime socket per tab, for everything under AuthLayout.
 *
 * Events are dispatched by envelope `type`. Each one is a hint carrying the
 * authoritative value, patched straight into the RTK Query cache; REST stays
 * the source of truth, so every open - first or reconnect - invalidates what
 * the socket keeps current, to cover whatever was missed while it was down.
 */
export const RealtimeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const url = getRealtimeURL();
  const value = useMemo(() => ({ connected }), [connected]);

  return (
    <RealtimeContext.Provider value={value}>
      {url && <RealtimeConnection url={url} onConnectedChange={setConnected} />}
      {children}
    </RealtimeContext.Provider>
  );
};

// Its own component so the store is only reached for when there is a socket to
// run: with no endpoint configured, AuthLayout needs nothing it did not before.
const RealtimeConnection: React.FC<{
  url: string;
  onConnectedChange: (connected: boolean) => void;
}> = ({ url, onConnectedChange }) => {
  const store = useStore<RootState>();

  useEffect(() => {
    const dispatch = store.dispatch as AppDispatch;
    const devices = createDeviceStateHandler({
      dispatch,
      getState: store.getState,
    });
    const handlers: Record<string, (envelope: RealtimeEnvelope) => void> = {
      "device.state": (envelope) => {
        if (isDeviceStateEnvelope(envelope)) devices.handle(envelope);
      },
    };

    // The organization the current ticket was minted for. Switching in this
    // tab reloads the page; this catches a switch made in another tab, which
    // moves this tab's requests to the new organization through the shared
    // localStorage selection and so must move its socket too.
    let ticketOrg = getActiveOrg();

    const socket = new RealtimeSocket({
      url,
      readTokenExpiry: async () => readJwtExpiry(await getAccessToken()),
      mintTicket: async () => {
        ticketOrg = getActiveOrg();
        const request = dispatch(
          realtimeApi.endpoints.createRealtimeTicket.initiate(),
        );
        try {
          return (await request.unwrap()).ticket;
        } finally {
          // A spent ticket has no business sitting in the store.
          request.reset();
        }
      },
      onEvent: (envelope) => handlers[envelope.type]?.(envelope),
      onOpen: () => dispatch(deviceApi.util.invalidateTags(["Device"])),
      onConnectedChange,
    });

    const onStorage = () => {
      if (getActiveOrg() !== ticketOrg) socket.reconnect();
    };
    // The network coming back ends the wait for a connection that is down;
    // an open one is left alone.
    const onOnline = () => {
      if (!socket.open) socket.reconnect();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("online", onOnline);
    socket.start();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("online", onOnline);
      socket.stop();
      devices.dispose();
    };
  }, [store, url, onConnectedChange]);

  return null;
};

export default RealtimeProvider;
